import { setSearchParams } from "./url";
import { WKT } from "ol/format";
import { displayPopup, setPopupPosition } from "./popup";
import { zoomToExtent, getLineStringCenter } from "./utils";
import { getCenter } from "ol/extent";
import { searchUrl } from "./config";

export function initSearch(map) {
  document.getElementById("search-query").addEventListener("input", (evt) => {
    const { query, filters, match } = getSearchParameters();
    if (query.length >= 3) {
      performSearch(query, filters, match)
        .then((results) => populateDropdown(map, results))
        .catch((error) => {
          console.error("Search failed:", error);
          clearDropdown();
        });
    } else {
      clearDropdown();
    }
    setSearchParams();
  });

  document.getElementById("search-query").addEventListener("keyup", (evt) => {
    if (evt.key === "Enter") {
      evt.preventDefault();
      triggerSearch(map);
      setSearchParams();
    }
  });

  document.getElementById("search-match").addEventListener("change", () => {
    triggerSearch(map);
    setSearchParams();
  });

  document.querySelectorAll('#search-filters input[type="checkbox"]').forEach((checkbox) => {
    checkbox.addEventListener("change", () => {
      triggerSearch(map);
      setSearchParams();
    });
  });

  document.getElementById("auto-popup-checkbox").addEventListener("change", () => {
    triggerSearch(map);
    setSearchParams();
  });

  document.addEventListener("click", (event) => {
    const dropdown = document.getElementById("search-dropdown");
    // Check if the clicked area is outside the dropdown
    if (!dropdown.contains(event.target)) {
      closeDropdown();
    }
  });
}

// Trigger a search based on the current search parameters
export function triggerSearch(map) {
  // clearDrawParams();
  const { query, match, filters } = getSearchParameters();
  if (query.length < 3) {
    document.getElementById("search-message").textContent = "Sökningen måste bestå av minst tre bokstäver";
    document.getElementById("search-message").style.display = "block";
  } else {
    document.getElementById("search-message").style.display = "none";
    performSearch(query, filters, match)
      .then((results) => handleSearchResults(results, map))
      .catch((error) => {
        console.error("Search failed:", error);
        document.getElementById("search-message").textContent = "Sökningen misslyckades, försök igen";
        document.getElementById("search-message").style.display = "block";
      });
  }
}

function populateDropdown(map, results) {
  const dropdown = document.getElementById("search-dropdown");
  dropdown.innerHTML = ""; // Clear existing items
  dropdown.style.display = results.length > 0 ? "block" : "none";

  results.forEach((result) => {
    const item = document.createElement("div");
    item.className = "dropdown-item";

    // Create a div for NAMN
    const nameDiv = document.createElement("div");
    nameDiv.className = "dropdown-name";
    nameDiv.textContent = result.NAMN;

    // Create a div for TYPE
    const typeDiv = document.createElement("div");
    typeDiv.className = "dropdown-type";
    typeDiv.textContent = result.TYPE;

    // Append both divs to the item
    item.appendChild(nameDiv);
    item.appendChild(typeDiv);

    item.onclick = () => {
      document.getElementById("search-query").value = result.NAMN;
      closeDropdown();
      triggerSearch(map);
      setSearchParams();
    };

    dropdown.appendChild(item);
  });
}

function clearDropdown() {
  const dropdown = document.getElementById("search-dropdown");
  dropdown.innerHTML = "";
}

function closeDropdown() {
  const dropdown = document.getElementById("search-dropdown");
  dropdown.style.display = "none"; // Hide the dropdown
}

function getSearchParameters() {
  const query = document.getElementById("search-query").value;
  const match = document.getElementById("search-match").value;
  const filterCheckboxes = document.querySelectorAll('#search-filters input[type="checkbox"]:checked');
  let filters = Array.from(filterCheckboxes).map((checkbox) => checkbox.value);

  return { query, match, filters };
}

// Perform a search based on the query and filters, rejects if the search fails
function performSearch(query, filters, matchType) {
  // Encode the parameters so the query can't add or cut off parameters in the request
  const params = new URLSearchParams({ q: query });

  if (filters.length > 0) {
    params.set("filter", filters.join(","));
  }

  if (matchType) {
    params.set("match", matchType);
  }

  return fetch(`${searchUrl}?${params}`)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Search request failed with status ${response.status}`);
      }
      return response.json();
    })
    .then((results) => {
      if (!Array.isArray(results)) {
        throw new Error("Search response is not a list of results");
      }
      return results;
    });
}

function handleSearchResults(results, map) {
  const searchMessage = document.getElementById("search-message");
  let markerSource = map
    .getLayers()
    .getArray()
    .find((layer) => layer.get("name") === "markerLayer")
    .getSource();

  // Skip results without a readable geometry instead of failing the whole search
  const wktFormat = new WKT();
  const features = results.flatMap((result) => {
    try {
      const feature = wktFormat.readFeature(result.GEOM, {
        dataProjection: "EPSG:3008",
        featureProjection: map.getView().getProjection(),
      });
      if (!feature.getGeometry()) {
        throw new Error("Result has no geometry");
      }
      feature.setProperties({
        type: result.TYPE,
        name: result.NAMN,
      });
      return [feature];
    } catch (error) {
      console.error("Invalid search result:", result, error);
      return [];
    }
  });

  if (features.length === 0) {
    searchMessage.textContent = "Sökningen gav inga resultat";
    searchMessage.style.display = "block";
    markerSource.clear();
  } else {
    searchMessage.style.display = "none";

    markerSource.clear();
    markerSource.addFeatures(features);

    const autoPopup = document.getElementById("auto-popup-checkbox").checked;

    if (features.length === 1 && autoPopup) {
      const feature = markerSource.getFeatures()[0];
      const geometry = feature.getGeometry();
      const geometryType = geometry.getType();
      let coordinates;

      switch (geometryType) {
        case "Point":
          coordinates = geometry.getCoordinates();
          break;
        case "LineString":
          coordinates = getLineStringCenter(geometry);
          break;
        case "Polygon":
          coordinates = geometry.getInteriorPoint().getCoordinates();
          break;
        case "MultiPoint":
        case "MultiLineString":
        case "MultiPolygon":
          coordinates = getCenter(geometry.getExtent());
          break;
        default:
          coordinates = geometry.getClosestPoint(map.getView().getCenter());
          break;
      }

      displayPopup(feature);
      setPopupPosition(coordinates);
    }

    zoomToExtent(map, markerSource.getExtent());
  }
}
